import React, { useMemo } from 'react'

import { Button, Grid, Typography, Box } from '@barracuda-internal/bds-core'

import InboundMachineLearning from 'components/pages/support/help/InboundMachineLearning/InboundMachineLearning'
import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import { PageTitle, FormSpacer, FormRow, InlineRadioGroup } from 'components/libs/layout/pages/pageLayout'
import { useMachineLearningLogic } from 'components/pages/inboundSettings/machineLearning/useMachineLearningLogic'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { useSettingsStateLogic } from 'components/libs/useSettingsState'
import { AvailableSettings } from 'types/Settings'
import { GROUP_TITLE, TOPICS } from 'components/pages/support/config'
import DomainTable from 'components/libs/domainTable/DomainTable'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import NoDomainPolicyAccessAlert from 'components/libs/layout/pages/NoDomainPolicyAccessAlert'

const BASE_I18N_KEY = 'ess.inbound_settings.machine_learning'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'

const MachineLearning: React.FC = () => {
  const isReadonly = useIsReadonly()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)

  const [state, eventHandlers] = useMachineLearningLogic()
  const [UseGetSettingsStateLogic] = useSettingsStateLogic()

  return useMemo(
    () => (
      <SettingsPageContainer dirtyCheck isDirtyForm={state.hasPageChanges}>
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={eventHandlers.helpConfig.onHelpClick}
          help={{
            content: InboundMachineLearning,
            onClose: eventHandlers.helpConfig.onCloseHelp,
            title: TOPICS.machineLearning,
            group: GROUP_TITLE.inboundSettings,
            isHelpOpen: eventHandlers.helpConfig.isOpen
          }}
        />
        {state.hasNoDomainPolicyAccess && <NoDomainPolicyAccessAlert />}
        {!state.hasNoDomainPolicyAccess && (
          <Box>
            <FormSpacer />
            {state.settings?.subscription_bomb_action !== undefined && (
              <>
                <Grid container alignItems="flex-start" spacing={2}>
                  <Grid item xs={12} sm={9}>
                    <Typography variant="h6" id="subscription-bomb-detection-label">
                      {formatMessage('subscription_bomb_detection')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
                      {formatMessage('subscription_bomb_description')}
                    </Typography>
                    <a
                      href="https://campus.barracuda.com/doc/96026236/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none', fontSize: '14px' }}
                    >
                      {formatMessage('subscription_bomb_learn_more')}
                    </a>
                  </Grid>
                  <Grid item xs={12} sm={3} style={{ paddingTop: '6px' }}>
                    {state.canQuarantineSettings ? (
                      <InlineRadioGroup
                        name={AvailableSettings.SUBSCRIPTION_BOMB_ACTION}
                        labels={[formatMessage('block'), formatMessage('quarantine')]}
                        value={state.settings.subscription_bomb_action}
                        values={['block', 'quarantine']}
                        onChange={eventHandlers.onFormChange(AvailableSettings.SUBSCRIPTION_BOMB_ACTION)}
                        disabled={
                          UseGetSettingsStateLogic.isLoading ||
                          isReadonly ||
                          state.hasPageChanges ||
                          !state.canManageDomainPolicies
                        }
                        aria-labelledby="subscription-bomb-detection-label"
                      />
                    ) : (
                      <Typography variant="body1">{formatMessage('block')}</Typography>
                    )}
                  </Grid>
                </Grid>
                <FormSpacer />
              </>
            )}
            <Grid container>
              <Grid container className={inboundClasses.spaceGrid}>
                <Typography variant="h4">{formatMessage('machine_learning_exempt')}</Typography>
                <Button
                  className={inboundClasses.bulkEdit}
                  onClick={eventHandlers.onBulkEdit}
                  variant="contained"
                  size="small"
                  data-testid="bulk-button"
                  disabled={isReadonly}
                >
                  {formatInboundMessage('bulk_edit')}
                </Button>
              </Grid>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                {formatMessage('machine_learning_exempt_description')}
              </Typography>
              <DomainTable
                data={state.tableData}
                onAddItem={eventHandlers.onAddItem}
                onRemoveItem={eventHandlers.onRemoveItem}
                shouldHandleEmail
                data-testid="machine-learning-exempt-table"
                delegateDirtyFormState={eventHandlers.setIsMachineLearningTableDirty}
              />
            </Grid>
          </Box>
        )}
      </SettingsPageContainer>
    ),
    [
      state.hasPageChanges,
      state.hasNoDomainPolicyAccess,
      state.canManageDomainPolicies,
      state.canQuarantineSettings,
      state.settings?.subscription_bomb_action,
      state.tableData,
      formatMessage,
      formatInboundMessage,
      eventHandlers.helpConfig.onHelpClick,
      eventHandlers.helpConfig.onCloseHelp,
      eventHandlers.helpConfig.isOpen,
      eventHandlers.onFormChange,
      eventHandlers.onBulkEdit,
      eventHandlers.onAddItem,
      eventHandlers.onRemoveItem,
      eventHandlers.setIsMachineLearningTableDirty,
      inboundClasses.spaceGrid,
      inboundClasses.bulkEdit,
      UseGetSettingsStateLogic.isLoading,
      isReadonly
    ]
  )
}

export default MachineLearning
